// One test's reach as the shared diagram's cards and lines (reachGraph.ts): the test card, with
// the types it fakes and the EF Core entities it reaches, then the files and members reached from
// the test method and what runs with it.
import type { CodeMap, CodeTest, TestFramework, TestReach } from '@modelviewer/core';
import type { Graph, Measure, PropertyMode } from '../diagram/graph';
import { codeIndex } from './codeIndex';
import { buildReachGraph, headRow } from './reachGraph';

/** The test's card id: never a file path. */
export const TEST_CARD = '@test';

export const FRAMEWORK_NAMES: Record<TestFramework, string> = { xunit: 'xunit', nunit: 'NUnit', mstest: 'MSTest', tunit: 'TUnit' };

/** `Theory, 3 cases` */
export function testKind(t: CodeTest): string {
  return t.cases ? `${t.attribute}, ${t.cases} ${t.cases === 1 ? 'case' : 'cases'}` : t.attribute;
}

/** `OrderServiceTests` for `Shop.Tests.Services.OrderServiceTests`. */
export function typeName(id: string): string {
  const name = id.slice(Math.max(id.lastIndexOf('.'), id.lastIndexOf('+')) + 1);
  return name.replace(/`\d+$/, '');
}

export function buildTestGraph(map: CodeMap, reach: TestReach, options: { members: PropertyMode; measure?: Measure }): Graph {
  const index = codeIndex(map);
  const { test } = reach;
  return buildReachGraph(
    map,
    reach,
    {
      id: TEST_CARD,
      key: test.id,
      label: test.name,
      badges: [FRAMEWORK_NAMES[test.framework], testKind(test), ...(test.skipped ? ['skipped'] : [])],
      table: typeName(test.type),
      // The test card lists what the test fakes, then the data it reaches.
      lines: [
        ...reach.fakes.map((f) => headRow(`fake:${f.type}`, index.types.get(f.type)?.name ?? typeName(f.type), f.by ? 'faked' : 'mocked', 'fake')),
        ...reach.data.map((id) => headRow(`data:${id}`, index.types.get(id)?.name ?? id, 'data', 'data')),
      ],
      starts: [{ id: 'test', member: test.id, label: 'runs' }, ...test.setup.filter((id) => reach.depth.has(id)).map((id) => ({ id: `setup:${id}`, member: id, label: 'runs with the test' }))],
    },
    options,
  );
}
